<script lang="ts">
    import IconButton from "../buttons/IconButton.svelte";
    import DropdownMenu from "../menu/dropdown/DropdownMenu.svelte";
    import DropdownMenuItem from "../menu/dropdown/DropdownMenuItem.svelte";
    import DropdownIcon from "../icons/DropdownIcon.svelte";
    import DeleteIcon from "../icons/DeleteIcon.svelte";
    import AddToTreeModal from "./Upload.svelte";
    import UploadIcon from "../icons/UploadIcon.svelte";
    import FolderIcon from "../icons/FolderIcon.svelte";
    import ScreenplayIcon from "../icons/ScreenplayIcon.svelte";
    import NotesIcon from "../icons/NotesIcon.svelte";
    import DropdownMenuSeparator from "../menu/dropdown/DropdownMenuSeparator.svelte";
    import ajax from "../../ajax/ajax";
    import Snackbar from "../snackbar/Snackbar.svelte";
    import RenameIcon from "../icons/RenameIcon.svelte";
    import type { TDirectoryTree } from "./TDirectoryTree";
    import { onMount } from "svelte";
    import ArrowRight from "../icons/ArrowRight.svelte";
    import ArrowDropdown from "../icons/ArrowDropdown.svelte";
    import FileIcon from "../icons/FileIcon.svelte";
    import getProjectStore from "../../stores/ProjectStore";
    import DownloadIcon from "../icons/DownloadIcon.svelte";
    import getToken from "../../auth/token";
    import { baseUrl } from "../../config";
    import SettingsIcon from "../icons/SettingsIcon.svelte";
    import { push } from "svelte-spa-router";

    export let tree: TDirectoryTree;
    export let open: string[] = [];
    export let parentPath: string;
    export let projectId: number;

    $: hasChildren = tree.children && tree.children.length > 0;
    $: isOpen = open.indexOf(tree.id) !== -1;
    let showCommands = false;
    let showUploader = false;
    let snackbarErrorMessage: string | null = null;

    const projectStore = getProjectStore(projectId);

    function addItem(type: "folder" | "screenplay" | "notes") {
        let extension: string;
        switch (type) {
            case "screenplay":
                extension = ".fountain";
                break;
            case "notes":
                extension = ".notes";
                break;
        }
        const newItem: TDirectoryTree = {
            name: "Unnamed",
            size: 0,
            type: type === "folder" ? "directory" : "file",
            extension: extension,
            new: true,
            id: "new",
        };
        projectStore.add(newItem, tree.path);
    }

    function editNewItem(node: HTMLInputElement, treeItem: TDirectoryTree) {
        node.focus();
        node.setSelectionRange(0, node.value.length);
        let allowSave = true;

        function errorCallback(message?: string) {
            snackbarErrorMessage = message ?? "Failed";
            node.focus();
        }

        function save() {
            if (
                treeItem.extension &&
                !treeItem.name.endsWith(treeItem.extension)
            ) {
                treeItem.name += treeItem.extension;
            }
            ajax.put(
                `/projects/${projectId}/tree`,
                {
                    path: parentPath,
                    treeItem,
                },
                (data: { ack: boolean; item: TDirectoryTree }) => {
                    if (data.ack) {
                        allowSave = false;
                        projectStore.replace(tree, data.item);
                        snackbarErrorMessage = null;
                    } else {
                        errorCallback();
                    }
                },
                (statusText: string, status: number, xhr: XMLHttpRequest) => {
                    try {
                        const returnValue = JSON.parse(xhr.responseText);
                        if (returnValue && returnValue.errorMessage) {
                            errorCallback(returnValue.errorMessage);
                            return;
                        }
                    } catch {}
                    errorCallback();
                }
            );
        }

        node.addEventListener("keydown", (e) => {
            if (e.key == "Enter") {
                e.stopPropagation();
                e.preventDefault();
                save();
            }
            if (e.key == "Escape") {
                allowSave = false;
                e.stopPropagation();
                e.preventDefault();
                projectStore.removeItem(treeItem);
            }
        });

        node.addEventListener("input", () => {
            treeItem.name = node.value;
        });

        node.addEventListener("blur", (e) => {
            e.stopPropagation();
            if (allowSave) {
                save();
            }
        });
    }

    function renameItem(node: HTMLInputElement, treeItem: TDirectoryTree) {
        let updatedName = treeItem.name;
        node.focus();
        node.setSelectionRange(0, node.value.length);
        let allowSave = true;

        function errorCallback(message?: string) {
            snackbarErrorMessage = message ?? "Rename failed";
            node.focus();
        }

        function save() {
            if (treeItem.extension && !updatedName.match(/.+\.[A-Za-z0-9]+$/)) {
                updatedName += treeItem.extension;
            }
            ajax.patch(
                `/projects/${projectId}/tree`,
                {
                    path: tree.path,
                    name: updatedName,
                },
                (data: { ack: boolean; item?: TDirectoryTree }) => {
                    if (data.ack) {
                        allowSave = false;
                        if (data.item) {
                            projectStore.replace(treeItem, data.item);
                        } else {
                            projectStore.replace(treeItem, {
                                ...tree,
                                renamed: false,
                            });
                        }
                        snackbarErrorMessage = null;
                    } else {
                        errorCallback();
                    }
                },
                (statusText: string, status: number, xhr: XMLHttpRequest) => {
                    try {
                        const returnValue = JSON.parse(xhr.responseText);
                        if (returnValue && returnValue.errorMessage) {
                            errorCallback(returnValue.errorMessage);
                            return;
                        }
                    } catch {}
                    errorCallback();
                }
            );
        }

        node.addEventListener("keydown", (e) => {
            if (e.key == "Enter") {
                e.stopPropagation();
                e.preventDefault();
                save();
            }
            if (e.key == "Escape") {
                allowSave = false;
                e.stopPropagation();
                e.preventDefault();
                projectStore.replace(treeItem, { ...tree, renamed: false });
            }
        });

        node.addEventListener("input", () => {
            updatedName = node.value;
        });

        node.addEventListener("blur", (e) => {
            e.stopPropagation();
            if (allowSave) {
                save();
            }
        });
    }

    function remove() {
        ajax.delete(
            `/projects/${projectId}/tree`,
            (data: { ack: boolean }) => {
                if (data.ack) {
                    projectStore.removeItem(tree);
                    snackbarErrorMessage = null;
                } else {
                    snackbarErrorMessage = "Could not remove.";
                }
            },
            (statusText: string, status: number, xhr: XMLHttpRequest) => {
                try {
                    const returnValue = JSON.parse(xhr.responseText);
                    if (returnValue && returnValue.errorMessage) {
                        snackbarErrorMessage = returnValue.errorMessage;
                        return;
                    }
                } catch {}
                snackbarErrorMessage = "Could not remove.";
            },
            {
                path: tree.path,
            }
        );
    }

    function move(paths: string[]) {
        ajax.post(
            `/projects/${projectId}/tree/move`,
            {
                targetPath: tree.path,
                movedPaths: paths,
            },
            (data: { ack: boolean; tree: TDirectoryTree }) => {
                if (data.ack) {
                    projectStore.replaceTree(data.tree);
                    snackbarErrorMessage = null;
                } else {
                    snackbarErrorMessage = "Could not move.";
                }
            },
            (statusText: string, status: number, xhr: XMLHttpRequest) => {
                try {
                    const returnValue = JSON.parse(xhr.responseText);
                    if (returnValue && returnValue.errorMessage) {
                        snackbarErrorMessage = returnValue.errorMessage;
                        return;
                    }
                } catch {}
                snackbarErrorMessage = "Could not move.";
            }
        );
    }

    function thresholdReached(dx: number, dy: number): boolean {
        const thresholdPixels = 10;
        const thresholdXReached =
            (dx > 0 && dx >= thresholdPixels) ||
            (dx < 0 && dx <= thresholdPixels * -1);
        const thresholdYReached =
            (dy > 0 && dy >= thresholdPixels) ||
            (dy < 0 && dy <= thresholdPixels * -1);

        return thresholdXReached || thresholdYReached;
    }

    let moveIndicator: HTMLElement;
    function draggable(node: HTMLElement) {
        if (tree.root) {
            return;
        }

        let x: number,
            y: number,
            moveActive = false;

        const mouseMove = (e: MouseEvent) => {
            let dx = e.clientX - x;
            let dy = e.pageY - y;
            if (!moveActive && thresholdReached(dx, dy)) {
                moveActive = true;
                moveIndicator.innerHTML = `<span>${tree.name}</span>`;
                moveIndicator.style.display = "block";
                moveIndicator.style.position = "fixed";
            }

            moveIndicator.style.left = `${e.clientX}px`;
            moveIndicator.style.top = `${e.clientY}px`;

            const event = new CustomEvent("moveTreeItem", {
                detail: {
                    node: moveIndicator,
                    paths: [tree.path],
                },
            });
            window.dispatchEvent(event);
        };

        const mouseUp = (e: MouseEvent) => {
            window.removeEventListener("mousemove", mouseMove);
            window.removeEventListener("mouseup", mouseUp);
            moveIndicator.style.display = "none";
            if (moveActive) {
                const event = new CustomEvent("stopMoveTreeItem", {
                    detail: {
                        paths: [tree.path],
                    },
                });
                window.dispatchEvent(event);
            }

            moveActive = false;
        };

        const mouseDown = (e: MouseEvent) => {
            x = e.clientX;
            y = e.clientY;
            window.addEventListener("mousemove", mouseMove);
            window.addEventListener("mouseup", mouseUp);
        };

        node.addEventListener("mousedown", mouseDown);
    }

    let droppable: HTMLElement;
    const onMoveTreeItem = (e: CustomEvent) => {
        if (
            tree.type === "directory" &&
            droppable &&
            e.detail.paths.indexOf(tree.path) === -1
        ) {
            const rectDragged = e.detail.node.getBoundingClientRect();
            const rectDropZone = droppable.getBoundingClientRect();

            // -20 = margin
            if (
                rectDragged.top > rectDropZone.top &&
                rectDragged.top < rectDropZone.bottom &&
                rectDragged.left - 20 > rectDropZone.left &&
                rectDragged.left - 20 < rectDropZone.right
            ) {
                tree.droppedOn = true;
            } else {
                tree.droppedOn = false;
            }
        }
    };

    const onStopMoveTreeItem = (e: CustomEvent) => {
        if (tree.droppedOn) {
            move(e.detail.paths);
            tree.droppedOn = false;
        }
    };
    onMount(() => {
        window.addEventListener("moveTreeItem", onMoveTreeItem);
        window.addEventListener("stopMoveTreeItem", onStopMoveTreeItem);
        return () => {
            window.removeEventListener("moveTreeItem", onMoveTreeItem);
            window.removeEventListener("stopMoveTreeItem", onStopMoveTreeItem);
        };
    });

    let icon = FileIcon;
    if (tree.type === "directory") {
        icon = FolderIcon;
    } else {
        switch (tree.extension) {
            case ".fountain":
                icon = ScreenplayIcon;
                break;
            case ".note":
            case ".md":
                icon = NotesIcon;
                break;
        }
    }
</script>

{#if tree}
    <li class="TreeItem">
        <div
            class="Item"
            class:Item--hasChildren={hasChildren}
            class:Item--root={tree.root}
            class:Item--droppedOn={tree.droppedOn}
            use:draggable
            bind:this={droppable}
            on:click={() => {
                if (tree.type === "file") {
                    projectStore.setShownInEditor(tree.id, true);
                }
            }}
        >
            <div
                class="Opener"
                on:mousedown|stopPropagation={() => {}}
                on:click|stopPropagation={() => {}}
            >
                {#if hasChildren && !tree.root}
                    <IconButton
                        title={isOpen ? "Close" : "Open"}
                        icon={isOpen ? ArrowDropdown : ArrowRight}
                        onClick={() => {
                            projectStore.setOpen(tree.id, !isOpen);
                        }}
                        color="#333"
                        height="24px"
                        width="24px"
                    />
                {/if}
            </div>
            {#if !tree.root}
                <svelte:component this={icon} height="24px" width="24px" />
            {/if}
            <div
                class="Label"
                on:contextmenu={(e) => {
                    e.preventDefault();
                    if (!tree.new) {
                        showCommands = !showCommands;
                    }
                }}
            >
                {#if tree.new}
                    <input
                        type="text"
                        value={tree.name}
                        use:editNewItem={tree}
                    />
                {:else if tree.renamed}
                    <input
                        type="text"
                        value={tree.name}
                        use:renameItem={tree}
                    />
                {:else}
                    {tree.name}
                {/if}
            </div>
            {#if !tree.new}
                <div
                    class="Commands"
                    on:click|stopPropagation={() => {}}
                    on:mousedown|stopPropagation={() => {}}
                >
                    <IconButton
                        title="Commands"
                        icon={DropdownIcon}
                        onClick={() => {
                            showCommands = !showCommands;
                        }}
                        color={tree.root ? "#333" : "#B2B2B2"}
                    />
                    {#if showCommands}
                        <div
                            style="position: absolute; top: 20px; left: 0; z-index: 2;"
                        >
                            <DropdownMenu
                                closeCallback={() => {
                                    showCommands = false;
                                }}
                            >
                                {#if tree.type === "directory"}
                                    <DropdownMenuItem
                                        title="Add folder"
                                        onClick={() => {
                                            addItem("folder");
                                            showCommands = false;
                                        }}
                                        icon={FolderIcon}
                                    />
                                    <DropdownMenuItem
                                        title="Add screenplay"
                                        onClick={() => {
                                            addItem("screenplay");
                                            showCommands = false;
                                        }}
                                        icon={ScreenplayIcon}
                                    />
                                    <DropdownMenuItem
                                        title="Add note"
                                        onClick={() => {
                                            addItem("notes");
                                            showCommands = false;
                                        }}
                                        icon={NotesIcon}
                                    />
                                    <DropdownMenuItem
                                        title="Upload"
                                        onClick={() => {
                                            showUploader = true;
                                            showCommands = false;
                                        }}
                                        icon={UploadIcon}
                                    />
                                    <DropdownMenuSeparator />
                                {:else}
                                    <DropdownMenuItem
                                        title="Download"
                                        icon={DownloadIcon}
                                        onClick={() => {
                                            showCommands = false;
                                            window.open(
                                                `${baseUrl}/projects/${projectId}/tree/file?path=${
                                                    tree.path
                                                }&token=${getToken()}`
                                            );
                                        }}
                                    />
                                {/if}
                                {#if !tree.root}
                                    <DropdownMenuItem
                                        title="Rename"
                                        onClick={() => {
                                            showCommands = false;
                                            tree.renamed = true;
                                            tree = tree;
                                        }}
                                        icon={RenameIcon}
                                    />
                                    <DropdownMenuItem
                                        title="Remove"
                                        onClick={() => {
                                            showCommands = false;
                                            if (
                                                confirm(`Delete ${tree.name}?`)
                                            ) {
                                                remove();
                                            }
                                        }}
                                        icon={DeleteIcon}
                                    />
                                    <DropdownMenuSeparator />
                                {:else}
                                    <DropdownMenuItem
                                        title="Project settings"
                                        icon={SettingsIcon}
                                        onClick={() => {
                                            push(
                                                `/project/${projectId}/settings`
                                            );
                                        }}
                                    />
                                    <DropdownMenuSeparator />
                                {/if}
                            </DropdownMenu>
                        </div>
                    {/if}
                </div>
            {/if}
        </div>

        {#if hasChildren && isOpen}
            <ul class="SubTree">
                {#each tree.children as child (child.id)}
                    <svelte:self
                        tree={child}
                        {open}
                        {projectId}
                        parentPath={tree.path}
                    />
                {/each}
            </ul>
        {/if}
        {#if showUploader}
            <AddToTreeModal
                path={tree.path}
                projectId={projectId}
                close={() => {
                    showUploader = false;
                }}
            />
        {/if}
        {#if snackbarErrorMessage}
            <Snackbar type="error" message={snackbarErrorMessage} />
        {/if}
        <div class="MoveIndicator" bind:this={moveIndicator} />
    </li>
{/if}

<style type="text/scss">
    @import "../../scss/config.scss";
    .Commands {
        position: relative;
    }
    .Label {
        margin: 0 5px;
    }
    .Item {
        user-select: none;
        display: flex;
        font-size: 18px;
        line-height: 24px;
        position: relative;
        padding-left: 16px;
        margin-bottom: 8px;
        &--droppedOn {
            .Label {
                background: $color-medium-blue;
                color: white;
            }
        }
        &--root {
            padding-left: 0;
            margin-bottom: 10px;
            .Label {
                font-weight: bold;
                font-size: 20px;
            }
        }
    }
    .Opener {
        position: absolute;
        width: 24px;
        height: 24px;
        left: -4px;
        top: -2px;
    }
    .MoveIndicator {
        margin-left: 20px;
        width: 1.5rem;
        height: 1.5rem;
        background: #fff;
        box-shadow: 0 0 8px #ccc;
        position: fixed;
        display: none;
        text-align: center;
        font-size: 1rem;
        line-height: 1.5rem;
        white-space: nowrap;
        color: rgb(85, 85, 182);
        border: 1px solid rgb(85, 85, 182);
        font-weight: bold;
    }
    .SubTree {
        list-style: none;
        padding: 0 0 0 10px;
        display: inline-block;
    }
</style>
